import { CenterSpinner } from '@/components/ats';
import { Flex, Icon, Link, Text } from '@chakra-ui/react';
import { useEffect, useState, useMemo } from 'react';
import { SelectedFilters } from '../components/filter/selected';
import { useDefaultReports } from '../home/reports';
import './printStyles.css';
import { DateRange } from './uiITems';
import { FaChartLine } from 'react-icons/fa';
import { KText } from '@/ui-library/components';
import { ReportBody } from '@/pages/reports/reports-main/components';
import { type ReportTypes } from '@/pages/reports/reports-main/types';
import { REPORT_CONSTANTS } from '@/pages/reports/reports-main/constants';
import { useGenerateReportsApi, useReportNetworkIdle } from './hooks';
import { useDispatch } from 'react-redux';
import { useKFlags } from '@/hooks/use-k-flags';
import { customReportActions } from '@/store/custom-report/customReportReducer';
import { staticReportActions } from '@/store/static-report/staticReportReducer';
import {
  LIVE_REPORT_SUBJECT_TYPE,
  TIME_RELATED_KEYS_LIVE_REPORTS,
} from '@/pages/reports/custom-reports/components/ReportTileContainer/constant';

declare global {
  interface Window {
    reportPdfGeneration: string;
  }
}

const { STATIC_REPORT_UI_RENDER_CONFIGS, getFiltersToShow } = REPORT_CONSTANTS;

// Map subject_type from API to static report IDs
const SUBJECT_TYPE_TO_STATIC_REPORT_MAP: Record<string, string> = {
  flow: 'flow-health',
  eeo: 'eeo',
  eeoc: 'eeo',
  pipeline: 'pipeline',
  hires: 'hires',
  jobs: 'jobs',
  // API subject_type stays `contacts`; maps to the renamed `candidates` slug.
  contacts: 'candidates',
  interviews: 'interviews',
  offers: 'offers',
  rejections: 'rejections',
  recruiter_performance: 'recruiter-performance',
  requisitions: 'requisitions',
  live_job_pipeline: 'live_job_pipeline',
};

const GenerateReports = () => {
  const { loading, reportData: fetchedReportData } = useGenerateReportsApi();
  const allReports = useDefaultReports({ isReadOnly: true });
  const dispatch = useDispatch();
  const { feFraudDetection } = useKFlags();
  const [currentReportData, setCurrentReport] = useState({
    name: null,
    icon: null,
    bgColor: null,
    color: null,
  });
  const [isReduxInitialized, setIsReduxInitialized] = useState(false);

  // Use fetched data from API, fallback to temp for testing
  const reportData = fetchedReportData; // || temp;

  const { subject_type } = reportData || {};

  // this hook is used to check if the network is idle and set the window.reportPdfGeneration variable
  useReportNetworkIdle();

  // Initialize Redux with the correct date filters from the payload
  // This must run BEFORE tiles render to prevent using default "past12m"
  // Live reports (e.g. live_job_pipeline) do not use timeframe - skip time-related init
  useEffect(() => {
    if (!reportData) {
      setIsReduxInitialized(false);
      return;
    }

    const isLiveReport = subject_type === LIVE_REPORT_SUBJECT_TYPE;

    if (!isLiveReport) {
      const dateFilters =
        reportData?.query?.filter_by?.date || reportData?.metadata?.timeFrame;
      const dateGroup =
        reportData?.query?.date_group ||
        reportData?.metadata?.timeFrame?.date_group;

      if (dateFilters?.from) {
        const timeFramePayload = {
          from: dateFilters.from,
          to: dateFilters.to || null,
        };

        if (subject_type === 'custom') {
          dispatch(customReportActions.applyTimeFrame(timeFramePayload));
          if (dateGroup) {
            dispatch(customReportActions.applyViewBy({ id: dateGroup }));
          }
        } else {
          dispatch(staticReportActions.applyTimeFrame(timeFramePayload));
          if (dateGroup) {
            dispatch(staticReportActions.applyViewBy({ id: dateGroup }));
          }
        }
      }
    }

    // Apply filters if present (strip time-related keys for live reports)
    if (
      reportData?.query?.filter_by &&
      Object.keys(reportData.query.filter_by).length > 0
    ) {
      const filters = { ...reportData.query.filter_by };
      if (isLiveReport) {
        TIME_RELATED_KEYS_LIVE_REPORTS.forEach((key) => {
          delete filters[key];
        });
      } else {
        delete filters.date; // Remove date as it's handled separately for non-live reports
      }

      if (Object.keys(filters).length > 0) {
        if (subject_type === 'custom') {
          dispatch(customReportActions.applyFilter(filters));
        } else {
          dispatch(staticReportActions.applyFilter(filters));
        }
      }
    }

    setIsReduxInitialized(true);
  }, [reportData, subject_type, dispatch]);

  const { from = undefined, to = undefined } =
    reportData?.metadata?.timeFrame || {};

  useEffect(() => {
    if (!subject_type || currentReportData?.name) {
      return;
    }

    const idMap = {
      eeoc: 'eeo',
      pipeline: 'pipelines',
      flow: 'flowhealth',
      recruiter_performance: 'recruiter-performance',
    };

    let currentReport = null;

    if (subject_type === 'custom') {
      const metadata = reportData?.metadata as
        | { name?: string; description?: string }
        | undefined;
      const { name = 'Custom Report', description = '' } = metadata ?? {};
      currentReport = {
        name,
        description,
        icon: FaChartLine,
        color: 'secondary-800',
      };
    } else {
      const subject = idMap[subject_type] || subject_type;
      currentReport = allReports?.find((i) => i?.id === subject);
    }

    if (currentReport) {
      setCurrentReport(currentReport);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject_type, reportData]);

  const renderHeader = () => {
    return (
      <Flex justifyContent="space-between" w="100%" alignItems="center">
        <Flex gap="6px" flexDir="row" alignItems="center">
          <Icon
            h="20px"
            w="20px"
            color={currentReportData?.color}
            as={currentReportData?.icon}
          />
          <KText variant="heading_2" textTransform="capitalize">
            {currentReportData?.name}
          </KText>
        </Flex>
        {subject_type !== 'live_job_pipeline' && (
          <Flex gap="11px" alignItems="center">
            <KText variant="heading_4">TIMEFRAME:</KText>
            <DateRange from={from} to={to} />
          </Flex>
        )}
      </Flex>
    );
  };

  // Transform data for ReportBody when using reports-main
  const transformedReportData = useMemo((): ReportTypes.ReportMeta | null => {
    // Handle custom reports
    const reportDataWithCustom = reportData as
      | {
          custom_report?: {
            id?: number;
            name?: string;
            tiles?: unknown[];
            layout?: unknown;
            filters?: unknown;
          };
          metadata?: { name?: string };
        }
      | undefined;
    if (subject_type === 'custom' && reportDataWithCustom?.custom_report) {
      const { custom_report, metadata } = reportDataWithCustom;
      const { tiles = [], layout = {}, filters } = custom_report;

      return {
        reportId: custom_report?.id?.toString() ?? '',
        type: 'custom' as ReportTypes.ReportType,
        isCustomReport: true,
        isStaticReport: false,
        isValid: true,
        isOwner: true,
        canModifyBlocks: false,
        filtersToShow: [],
        layoutData: {
          ...custom_report,
          tiles,
          layout,
        },
        defaultFilters: filters,
        title: custom_report.name || metadata?.name || 'Custom Report',
        isLoading: false,
        error: null,
      };
    }

    // Handle static reports
    const staticReportId = SUBJECT_TYPE_TO_STATIC_REPORT_MAP[subject_type];
    if (staticReportId && STATIC_REPORT_UI_RENDER_CONFIGS[staticReportId]) {
      const {
        layout: layoutData,
        filters,
        title,
      } = STATIC_REPORT_UI_RENDER_CONFIGS[staticReportId];

      // Merge saved tableGroups (from the user's breakdown selection at
      // download time) into each table tile's metadata.filters so the print
      // render reflects what the user saw in the live UI. Without this,
      // ReportTileContainer initializes localTableFilters from the static
      // config defaults and ignores the user's selection.
      const savedTableGroups = (
        reportData?.metadata as { tableGroups?: Record<string, unknown> }
      )?.tableGroups;
      const enhancedLayoutData =
        layoutData && savedTableGroups
          ? {
              ...(layoutData as { tiles?: unknown[] }),
              tiles: (
                (layoutData as { tiles?: Array<Record<string, unknown>> })
                  .tiles || []
              ).map((tile) => {
                const meta = tile.metadata as
                  | { tileKey?: string; filters?: Record<string, unknown> }
                  | undefined;
                const key = meta?.tileKey;
                const userSelection = key
                  ? (savedTableGroups[key] as Record<string, unknown>)
                  : undefined;
                if (!userSelection) return tile;
                return {
                  ...tile,
                  metadata: {
                    ...(meta || {}),
                    filters: {
                      ...(meta?.filters || {}),
                      ...userSelection,
                    },
                  },
                };
              }),
            }
          : layoutData;

      return {
        reportId: staticReportId,
        type: 'static' as ReportTypes.ReportType,
        isCustomReport: false,
        isStaticReport: true,
        isValid: true,
        isOwner: false,
        canModifyBlocks: false,
        filtersToShow: getFiltersToShow(filters, feFraudDetection ?? false),
        layoutData: enhancedLayoutData || {},
        defaultFilters: reportData?.query?.filter_by || {},
        title: title || staticReportId,
        isLoading: false,
        error: layoutData ? null : 'Static report configuration not found',
      };
    }

    return null;
  }, [subject_type, reportData, feFraudDetection]);

  const selectedFilters = useMemo(() => {
    const metadata = reportData?.metadata;
    const existingFilters = metadata?.filtersToRender || [];

    return existingFilters;
  }, [reportData?.metadata]);

  if (loading || !isReduxInitialized) {
    return <CenterSpinner h="90vh" />;
  }

  // Show error if feReportsV3 is enabled but no transformed data available
  if (!transformedReportData) {
    return (
      <Flex mt="30px" flexDir="column" gap="10px" alignItems="center">
        <KText>Invalid Report</KText>
        <KText>
          Please contact us at{' '}
          <Link href="mailto:support@kula.ai" target="_blank">
            support@kula.ai
          </Link>
        </KText>
      </Flex>
    );
  }

  return (
    <Flex flexDir="column" h="100%" w="950px" id="divToPrint">
      {renderHeader()}
      {selectedFilters?.length > 0 && (
        <Flex
          bg="ats-bg-primary"
          p="15px"
          mt="24px"
          gap="8px"
          borderRadius="sm"
          flexWrap="wrap"
          w="100%"
          alignItems="center"
        >
          <Text size="sm-medium">Filters:</Text>
          <SelectedFilters
            isReadOnly
            selected={selectedFilters}
            onRemoveFilter={() => {}}
          />
        </Flex>
      )}
      <ReportBody reportData={transformedReportData} isPrintView />
    </Flex>
  );
};

export default GenerateReports;
